import dayjs from 'dayjs';
import { useCallback, useEffect, useRef } from 'react';

import { CommonActions, SlotActions } from '@Actions/index';
import { IConsultationInvoice } from '@Reducers/billing';
import { IQueryData } from '@Reducers/common';
import { IPurchasedConsultationData, IRescheduleConsultationData } from '@Reducers/consultation';
import { Selectables } from '@Reducers/selectables';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { ScheduleService } from '@Services';
import { InvoiceMode } from '@Services/Billing/billing-payload';
import {
  CONSULTATION_STATE,
  CONSULTATION_TYPE,
  DEFAULT_LOCATION_CODE,
  INSTANT_LOCATION_CODE,
  ONLINE_LOCATION_ID,
  PAGE,
  PHONE_CALL_LOCATION_CODE,
} from '@Utils/constants';
import { isOfflineLocation } from '@Utils/helper';
import useAnasRouter from '@Utils/hooks/anas-router';
import { Routes } from '@Utils/hooks/anas-router/routes';
import useBookConsultation from '@Utils/hooks/book-consultation';
import usePosthog, { CtaEvents } from '@Utils/hooks/posthog';
import useInvoice from '@Utils/hooks/preview-invoice';
import useRescheduleInstantConsultation from '@Utils/hooks/reschedule-instant-consultation';
import { LOG_ERROR } from '@Utils/logger';

import useConsultationType from '../../consultation-type';
import useGetDays from '../../get-days';
import useScheduleAppointment from '../../schedule-appointment';
import useTracking from '../../track-event';
import { Direction } from '../constants';
import useOnEnter from './on-enter';
import { Rule } from './type';

export const useCheckoutRule = (): Rule => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const { generatePreview, instantInvoicePreview, nonInstantInvoicePreview: consultationInvoicePreview } = useInvoice();
  const { scheduleAppointment: updateAppointment, isUpdatingAppointment } = useScheduleAppointment();
  const { rescheduleInstantConsultation, isUpdatingAppointment: isUpdatingInstantAppointment } =
    useRescheduleInstantConsultation();
  const bookConsultation = useBookConsultation();
  const { consultationType, locationType } = useConsultationType();
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const queryDataRef = useRef<IQueryData>(queryData);
  queryDataRef.current = queryData;
  const consultationInvoice: IConsultationInvoice = useAppSelector((state) => state.billing.consultationInvoice);
  const consultation: IRescheduleConsultationData = useAppSelector(
    (state) => state.consultation.rescheduleConsultationData,
  );
  const rescheduleConsultationData: IPurchasedConsultationData = useAppSelector(
    (state) => state.consultation.rescheduleConsultationData,
  );
  const orderConfirmed: boolean = useAppSelector((state) => state.order?.orderConfirmed);
  const { startDate, endDate } = useGetDays(consultationType, locationType, consultation?.dueDate);
  const trackSlots = useTracking(consultationType, locationType);
  const { ctaEventCapture } = usePosthog();

  const listSlots = useCallback(() => {
    dispatch(
      SlotActions.GetSlots({
        startDate: dayjs(startDate).toISOString(),
        endDate: dayjs(endDate).endOf('day').toISOString(),
        consultationTypeId: consultationType.consultationTypeId,
        providerIds: queryData?.providerId,
        excludedProviderIds: queryData?.excludedProviderId,
        preferredLanguages: locationType?.id === ONLINE_LOCATION_ID ? queryData?.selectedLanguages : [],
        locationId: locationType?.id,
        speciality: queryData?.speciality,
        flow: PAGE.APPOINTMENT.NAME,
      }),
    );
  }, [dispatch, consultationType, locationType, queryData]);

  const next = useCallback(
    (payload: { selectables: Selectables }) => {
      ctaEventCapture(CtaEvents.CHECKOUT);
      const { selectables } = payload;

      if (queryDataRef.current?.appointmentState && locationType.code === INSTANT_LOCATION_CODE) {
        rescheduleInstantConsultation({
          consultationId: queryDataRef.current?.consultationId,
        });
        return;
      }

      if (
        queryDataRef.current?.appointmentState &&
        (rescheduleConsultationData?.invoiceMode === InvoiceMode.Postpaid
          ? locationType.code !== DEFAULT_LOCATION_CODE
          : true)
      )
        updateAppointment({
          consultationId: queryDataRef.current?.consultationId,
          locationId: locationType?.id,
          appointmentState:
            queryDataRef.current?.appointmentState === CONSULTATION_STATE.RESCHEDULE &&
            (queryDataRef.current.consultationCode === CONSULTATION_TYPE.SC ||
              queryDataRef.current.consultationCode === CONSULTATION_TYPE.FU)
              ? CONSULTATION_STATE.SCHEDULE
              : queryDataRef.current?.appointmentState,
          selectedSlot: selectables?.slot,
          selectedLanguages: queryDataRef.current?.selectedLanguages,
          ...(queryDataRef?.current?.locationCode === PHONE_CALL_LOCATION_CODE && {
            appointmentMode: queryDataRef?.current?.locationCode,
          }),
          trackSlots: (event: string) => trackSlots(event, selectables.slot, PAGE.APPOINTMENT.NAME),
          version: 2,
        });
      else {
        const update = () => {
          bookConsultation({
            typeId: consultationType?.consultationTypeId,
            locationId: locationType?.id,
            selectedSlot: selectables.slot,
            payOnArrival: queryData?.invoiceMode === InvoiceMode.Postpaid,
            getSlots: listSlots,
            trackSlots: (event: string) => trackSlots(event, selectables.slot, PAGE.APPOINTMENT.NAME),
            noHold: true,
          });
        };
        if (
          rescheduleConsultationData?.invoiceMode === InvoiceMode.Postpaid &&
          locationType.code === DEFAULT_LOCATION_CODE
        ) {
          ScheduleService.UpdateConsultation({
            consultationId: queryDataRef.current?.consultationId,
            locationId: locationType?.id,
            blockId: '',
            startTime: '',
            reason: 'Changing mode to prepaid',
            consultationState: CONSULTATION_STATE.CANCEL,
          })
            .then(() => update())
            .catch((error) => {
              LOG_ERROR('Error updating consultation to online', error);
            });
        } else {
          update();
        }
      }
    },
    [router, consultationInvoice?.id, trackSlots],
  );

  const prev = useCallback(() => {
    router.push({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
  }, [router]);

  useOnEnter(() => {
    //temporarily setting postpaid as default
    dispatch(
      CommonActions.UpdateQueryData({
        invoice_mode:
          isOfflineLocation(queryData?.locationCode) && queryData?.consultationCode === CONSULTATION_TYPE.SC
            ? InvoiceMode.Postpaid
            : InvoiceMode.Prepaid,
      }),
    );
    generatePreview({
      voucherCode: queryData?.invoiceMode !== InvoiceMode.Postpaid ? queryData?.voucherCode : '',
      locationId: locationType?.id,
      consultationTypeId: consultationType?.consultationTypeId,
      invoiceMode: queryData.invoiceMode,
      locationCode: queryData?.locationCode,
    });
  }, [dispatch]);

  useEffect(() => {
    if (orderConfirmed && queryData?.locationCode === INSTANT_LOCATION_CODE) router.replace({ route: Routes.WAITING });
    else if (orderConfirmed) router.push({ route: Routes.APPOINTMENT, subRoute: Direction.CONFIRM_BOOKING });
  }, [orderConfirmed]);

  return {
    next: next,
    prev: prev,
    isLoading:
      consultationInvoice?.isLoading ||
      consultationInvoicePreview?.isLoading ||
      instantInvoicePreview?.isLoading ||
      !!isUpdatingAppointment.current ||
      !!isUpdatingInstantAppointment.current,
  };
};
